import React, { useState, useRef } from "react";
import { Upload, Link as LinkIcon, Download, Check, Sparkles, AlertCircle, X, Image as ImageIcon, Loader2, ExternalLink, HardDrive } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * ImageInputUploader
 * Universal Image Manager with Server-side WebP Optimization, Direct Upload, and Remote URL Caching
 */
export function ImageInputUploader({
  label,
  value = "",
  onChange,
  preset = "general", // "poster" | "backdrop" | "article" | "festival" | "avatar" | "thumbnail" | "general"
  aspectRatio = "auto", // "poster" (2/3) | "banner" (16/9) | "article" (16/10) | "square" (1/1) | "auto"
  helperText = "",
  required = false,
  className = ""
}) {
  const [activeTab, setActiveTab] = useState("file"); // "file" | "url"
  const [urlInput, setUrlInput] = useState(value || "");
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloadingUrl, setIsDownloadingUrl] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [lastStats, setLastStats] = useState(null);
  const fileInputRef = useRef(null);

  // Sync internal url state if value changes externally
  React.useEffect(() => {
    setUrlInput(value || "");
  }, [value]);

  const isLocalServer = value && (value.startsWith("/api/files/") || value.startsWith("/uploads/"));
  const isExternal = value && value.startsWith("http");

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Por favor selecciona un archivo de imagen válido (JPG, PNG, WEBP, AVIF)");
      return;
    }

    setIsUploading(true);
    setLastStats(null);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("preset", preset);

    try {
      const { data } = await api.post("/upload/image", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (data.url) {
        onChange(data.url);
        setUrlInput(data.url);
        if (data.original_size && data.optimized_size) {
          setLastStats({
            orig: formatBytes(data.original_size),
            opt: formatBytes(data.optimized_size),
            saved: data.saved_percent || 0
          });
          toast.success(`Imagen optimizada y guardada en servidor (-${data.saved_percent || 0}%)`);
        } else {
          toast.success("Imagen subida con éxito");
        }
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al subir y optimizar imagen");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownloadFromUrl = async (urlToDownload) => {
    const targetUrl = urlToDownload || urlInput;
    if (!targetUrl || !targetUrl.trim().startsWith("http")) {
      toast.error("Introduce una URL válida que empiece por http:// o https://");
      return;
    }

    setIsDownloadingUrl(true);
    setLastStats(null);
    try {
      const { data } = await api.post("/upload/from-url", {
        url: targetUrl.trim(),
        preset
      });

      if (data.url) {
        onChange(data.url);
        setUrlInput(data.url);
        const info = data.info || {};
        if (info.originalSize && info.optimizedSize) {
          setLastStats({
            orig: formatBytes(info.originalSize),
            opt: formatBytes(info.optimizedSize),
            saved: info.savedPercent || 0
          });
          toast.success(`Imagen descargada y optimizada en WebP (-${info.savedPercent || 0}%)`);
        } else {
          toast.success("Imagen guardada en nuestro servidor");
        }
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al descargar y procesar imagen remota");
    } finally {
      setIsDownloadingUrl(false);
    }
  };

  const handleApplyUrl = (e) => {
    e.preventDefault();
    if (!urlInput.trim()) {
      onChange("");
      return;
    }
    onChange(urlInput.trim());
    toast.success("URL de imagen asignada");
  };

  const handleClear = () => {
    onChange("");
    setUrlInput("");
    setLastStats(null);
  };

  // Aspect ratio classes for preview
  const getAspectClass = () => {
    switch (aspectRatio) {
      case "poster": return "aspect-[2/3] max-w-[150px]";
      case "banner": return "aspect-[16/9] max-w-[280px]";
      case "article": return "aspect-[16/10] max-w-[240px]";
      case "square": return "aspect-square max-w-[140px]";
      default: return "max-h-40 max-w-[220px]";
    }
  };

  return (
    <div className={`space-y-2.5 ${className}`}>
      {/* Header & Label */}
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-300 flex items-center gap-1.5">
          <ImageIcon size={14} className="text-[#E11D48]" />
          <span>{label}</span>
          {required && <span className="text-rose-500">*</span>}
        </label>

        {value && (
          <div className="flex items-center gap-2">
            {isLocalServer ? (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 font-medium">
                <HardDrive size={11} /> Servidor Local (WebP)
              </span>
            ) : isExternal ? (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/25 font-medium">
                <LinkIcon size={11} /> Enlace Externo
              </span>
            ) : null}
          </div>
        )}
      </div>

      {/* Main Container */}
      <div className="bg-[#0e0e11] border border-white/10 rounded-xl p-3 space-y-3">
        {/* Toggle Mode Tabs */}
        <div className="flex items-center gap-1 bg-zinc-900/90 p-1 rounded-lg border border-white/5">
          <button
            type="button"
            onClick={() => setActiveTab("file")}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-md text-xs font-medium transition-all ${activeTab === "file" ? "bg-[#E11D48] text-white shadow-sm" : "text-zinc-400 hover:text-zinc-200"}`}
          >
            <Upload size={13} />
            <span>Subir Archivo Local</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("url")}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-md text-xs font-medium transition-all ${activeTab === "url" ? "bg-[#E11D48] text-white shadow-sm" : "text-zinc-400 hover:text-zinc-200"}`}
          >
            <LinkIcon size={13} />
            <span>URL Web / TMDb</span>
          </button>
        </div>

        {/* TAB 1: File Upload / Drag & Drop */}
        {activeTab === "file" && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (e.dataTransfer.files?.[0]) handleFileUpload(e.dataTransfer.files[0]);
            }}
            onClick={() => fileInputRef.current?.click()}
            className={`cursor-pointer border-2 border-dashed rounded-xl p-4 text-center transition-all ${dragOver ? "border-[#E11D48] bg-[#E11D48]/10" : "border-white/15 hover:border-white/30 bg-zinc-900/40 hover:bg-zinc-900/70"}`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/jpg,image/avif"
              onChange={(e) => handleFileUpload(e.target.files?.[0])}
              className="hidden"
            />
            {isUploading ? (
              <div className="flex flex-col items-center justify-center py-2 space-y-2">
                <Loader2 size={24} className="animate-spin text-[#E11D48]" />
                <p className="text-xs font-medium text-zinc-300">Optimizando y subiendo a WebP...</p>
                <p className="text-[10px] text-zinc-500">Comprimiendo sin pérdida visual de calidad</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-1 space-y-1.5">
                <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-300">
                  <Upload size={16} />
                </div>
                <div className="text-xs text-zinc-300 font-medium">
                  <span className="text-[#E11D48] underline underline-offset-2">Haz clic para buscar</span> o arrastra tu imagen aquí
                </div>
                <p className="text-[10px] text-zinc-500">
                  JPG, PNG, WEBP · Auto-conversión a WebP optimizado ultraligero
                </p>
              </div>
            )}
          </div>
        )}

        {/* TAB 2: URL Input + Download to server option */}
        {activeTab === "url" && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://image.tmdb.org/... o https://..."
                className="flex-1 bg-zinc-900 border border-white/15 rounded-lg px-3 py-2 text-xs text-white placeholder:text-zinc-600 outline-none focus:border-[#E11D48]"
              />
              <button
                type="button"
                onClick={handleApplyUrl}
                className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-semibold rounded-lg transition-colors shrink-0"
              >
                Asignar
              </button>
            </div>

            {/* Quick action to download and host external URL */}
            {urlInput && urlInput.startsWith("http") && !isLocalServer && (
              <button
                type="button"
                onClick={() => handleDownloadFromUrl(urlInput)}
                disabled={isDownloadingUrl}
                className="w-full py-2 px-3 bg-gradient-to-r from-[#E11D48]/20 to-purple-600/20 hover:from-[#E11D48]/30 hover:to-purple-600/30 border border-[#E11D48]/40 rounded-lg text-xs font-semibold text-rose-300 hover:text-white flex items-center justify-center gap-2 transition-all disabled:opacity-50"
              >
                {isDownloadingUrl ? (
                  <>
                    <Loader2 size={13} className="animate-spin text-rose-400" />
                    <span>Descargando y optimizando en servidor...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={13} className="text-amber-400" />
                    <span>⚡ Guardar y Optimizar en nuestro Servidor (WebP)</span>
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Optimization Stats Banner */}
        {lastStats && (
          <div className="p-2.5 rounded-lg bg-emerald-950/40 border border-emerald-500/30 flex items-center justify-between text-xs text-emerald-300">
            <div className="flex items-center gap-1.5 font-medium">
              <Check size={14} className="text-emerald-400" />
              <span>Optimizado a WebP de alta fidelidad:</span>
            </div>
            <div className="font-mono text-[11px] text-zinc-300">
              <span className="line-through text-zinc-500">{lastStats.orig}</span> ➔ <strong className="text-emerald-400">{lastStats.opt}</strong> (-{lastStats.saved}%)
            </div>
          </div>
        )}

        {/* Live Image Preview & Actions */}
        {value && (
          <div className="pt-2 border-t border-white/10 flex items-start gap-3">
            <div className={`relative rounded-lg overflow-hidden border border-white/15 bg-zinc-950 shrink-0 ${getAspectClass()}`}>
              <img
                src={value}
                alt="Vista previa"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=400&auto=format&fit=crop&q=80";
                }}
              />
            </div>

            <div className="flex-1 min-w-0 space-y-2">
              <div className="space-y-0.5">
                <p className="text-xs font-medium text-white truncate">Imagen Seleccionada</p>
                <p className="text-[11px] text-zinc-500 font-mono truncate">{value}</p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {isExternal && (
                  <button
                    type="button"
                    onClick={() => handleDownloadFromUrl(value)}
                    disabled={isDownloadingUrl}
                    className="px-2.5 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[11px] font-medium rounded flex items-center gap-1 transition-colors"
                    title="Alojar una copia local de esta imagen en nuestro servidor para mayor velocidad y resiliencia"
                  >
                    {isDownloadingUrl ? <Loader2 size={11} className="animate-spin" /> : <HardDrive size={11} className="text-emerald-400" />}
                    <span>Alojar Local</span>
                  </button>
                )}

                <a
                  href={value}
                  target="_blank"
                  rel="noreferrer"
                  className="px-2.5 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white text-[11px] font-medium rounded flex items-center gap-1 transition-colors"
                >
                  <ExternalLink size={11} />
                  <span>Ver</span>
                </a>

                <button
                  type="button"
                  onClick={handleClear}
                  className="px-2.5 py-1 bg-rose-950/40 hover:bg-rose-900/60 text-rose-300 text-[11px] font-medium rounded flex items-center gap-1 transition-colors"
                >
                  <X size={11} />
                  <span>Quitar</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {helperText && (
          <p className="text-[11px] text-zinc-500 leading-relaxed">{helperText}</p>
        )}
      </div>
    </div>
  );
}
export default ImageInputUploader;
